/* LTHR sticky add-to-cart.
   Proxies the main product form's submit button so cart behavior, variant
   selection, and quantity always come from the single source of truth.
   Price sync subscribes to Dawn's variantChange pub/sub (scoped to the main
   product section) and re-queries the visible price node on every change,
   because Dawn replaces the price markup during variant updates. */

if (!customElements.get('ntd-sticky-atc')) {
  customElements.define(
    'ntd-sticky-atc',
    class NtdStickyAtc extends HTMLElement {
      connectedCallback() {
        this.submitProxy = this.querySelector('[data-ntd-sticky-submit]');
        this.priceEl = this.querySelector('[data-ntd-sticky-price]');
        this.labelEl = this.querySelector('[data-ntd-sticky-label]');
        this.productInfo = document.querySelector('product-info[data-section]');
        this.sectionId = this.productInfo ? this.productInfo.dataset.section : null;
        this.mainSubmit = (this.productInfo || document).querySelector('.product-form__submit');
        if (!this.mainSubmit || !this.submitProxy) return;

        this.submitProxy.addEventListener('click', () => {
          this.mainSubmit.click();
        });

        // Reveal only after the real buy button scrolls above the viewport.
        // A rAF-throttled scroll check (not IntersectionObserver) so instant
        // jumps — anchor links, fast flicks — can't skip the transition:
        // IO never fires when an element crosses the whole viewport in one
        // frame (not-intersecting before and after).
        this.updateReveal = () => {
          if (this.rafId) return;
          this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            if (this.mainSubmit.getBoundingClientRect().bottom < 0) {
              this.removeAttribute('hidden');
            } else {
              this.setAttribute('hidden', '');
            }
          });
        };
        window.addEventListener('scroll', this.updateReveal, { passive: true });
        window.addEventListener('resize', this.updateReveal, { passive: true });
        this.updateReveal();

        // The submit button node persists across variant swaps; mirror its
        // disabled/loading state and label from attribute mutations.
        this.syncObserver = new MutationObserver(() => this.sync());
        this.syncObserver.observe(this.mainSubmit, {
          attributes: true,
          childList: true,
          subtree: true,
        });

        // Dawn replaces price markup on variant change — re-query, don't observe.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const eventSectionId = event && event.data ? event.data.sectionId : undefined;
            if (!this.sectionId || !eventSectionId || eventSectionId === this.sectionId) {
              this.sync();
              this.syncComparisonImage(event);
            }
          });
        }

        // Quantity stepper: the CTA and sticky price show the line total
        // (Morgan 2026-09-16). Delegated so a re-rendered input still counts.
        this.quantityScope = this.productInfo || document;
        this.onQuantityChange = (event) => {
          if (event.target && event.target.classList && event.target.classList.contains('quantity__input')) this.sync();
        };
        this.quantityScope.addEventListener('change', this.onQuantityChange);
        this.quantityScope.addEventListener('input', this.onQuantityChange);

        this.sync();
      }

      disconnectedCallback() {
        if (this.updateReveal) {
          window.removeEventListener('scroll', this.updateReveal);
          window.removeEventListener('resize', this.updateReveal);
        }
        if (this.rafId) cancelAnimationFrame(this.rafId);
        if (this.syncObserver) this.syncObserver.disconnect();
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        if (this.quantityScope && this.onQuantityChange) {
          this.quantityScope.removeEventListener('change', this.onQuantityChange);
          this.quantityScope.removeEventListener('input', this.onQuantityChange);
        }
      }

      currentQuantity() {
        const scope = this.productInfo || document;
        const input = scope.querySelector('.product-form__quantity .quantity__input, input.quantity__input');
        const qty = input ? parseInt(input.value, 10) : 1;
        return qty > 0 ? qty : 1;
      }

      // Multiply the rendered money string so Dawn's currency format survives:
      // only the digits change ("$65.00" x 2 -> "$130.00", "$1,299.50" x 2 -> "$2,599.00").
      totalPriceText(unitText, qty) {
        if (!unitText || qty <= 1) return unitText;
        const match = unitText.match(/(\d[\d,]*)(?:\.(\d{2}))?/);
        if (!match) return unitText;
        const cents = parseInt(match[1].replace(/,/g, ''), 10) * 100 + (match[2] ? parseInt(match[2], 10) : 0);
        const total = cents * qty;
        const whole = Math.floor(total / 100)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const rem = total % 100;
        const num = match[2] || rem ? whole + '.' + String(rem).padStart(2, '0') : whole;
        return unitText.slice(0, match.index) + num + unitText.slice(match.index + match[0].length);
      }

      // The comparison card can show this product's own photo; keep it on the
      // variant the shopper picked (server renders the selected variant, this
      // covers in-page swaps). No-op when the variant has no image.
      syncComparisonImage(event) {
        const media = event && event.data && event.data.variant && event.data.variant.featured_media;
        const src = media && media.preview_image && media.preview_image.src;
        if (!src) return;
        const img = document.querySelector('[data-ntd-comparison-media] img');
        if (!img) return;
        const qIndex = src.indexOf('?');
        const base = qIndex === -1 ? src : src.slice(0, qIndex);
        const sep = qIndex === -1 ? '?' : src.slice(qIndex) + '&';
        img.srcset = [375, 550, 750, 1100, 1500]
          .map((w) => base + sep + 'width=' + w + ' ' + w + 'w')
          .join(', ');
        img.src = base + sep + 'width=1100';
      }

      currentPriceNode() {
        const scope = this.productInfo || document;
        const container = scope.querySelector('.price');
        if (!container) return null;
        const onSale =
          container.classList.contains('price--on-sale') ||
          container.querySelector('.price--on-sale') !== null;
        if (onSale) {
          const sale = container.querySelector('.price__sale .price-item--sale');
          if (sale) return sale;
        }
        return (
          container.querySelector('.price__regular .price-item--regular') ||
          container.querySelector('.price-item--regular') ||
          container.querySelector('.price-item')
        );
      }

      sync() {
        const disabled =
          this.mainSubmit.hasAttribute('disabled') ||
          this.mainSubmit.getAttribute('aria-disabled') === 'true';
        this.submitProxy.toggleAttribute('disabled', disabled);
        this.submitProxy.classList.toggle(
          'loading',
          this.mainSubmit.classList.contains('loading')
        );
        this.submitProxy.setAttribute('aria-busy', this.mainSubmit.classList.contains('loading') ? 'true' : 'false');

        const priceNode = this.currentPriceNode();
        const qty = this.currentQuantity();
        const unitText = priceNode ? priceNode.textContent.trim() : '';
        const totalText = this.totalPriceText(unitText, qty);
        if (priceNode && this.priceEl) this.priceEl.textContent = totalText;

        const mainLabel = this.mainSubmit.querySelector('span:not(.loading__spinner)');
        if (mainLabel) {
          // Reference pattern: the CTA reads "Add to Cart - [selected price]".
          // Only decorate the true add-to-cart state; Dawn's own sold-out /
          // unavailable labels pass through untouched. Guarded writes keep the
          // MutationObserver from looping.
          const strings = window.variantStrings || {};
          const base = mainLabel.textContent.trim();
          if (
            !disabled &&
            strings.addToCart &&
            priceNode &&
            (base === strings.addToCart || base.indexOf(strings.addToCart + ' -') === 0)
          ) {
            const wanted = strings.addToCart + ' - ' + totalText.replace(/\.00(?=\D*$)/, '');
            // The comparison card compares unit prices, so it keeps the single-item figure.
            const unitWanted = strings.addToCart + ' - ' + unitText.replace(/\.00(?=\D*$)/, '');
            const cmpLabel = document.querySelector('[data-ntd-comparison-atc-label]');
            if (cmpLabel && cmpLabel.textContent !== unitWanted) cmpLabel.textContent = unitWanted;
            if (base !== wanted) mainLabel.textContent = wanted;
          }
          if (this.labelEl) this.labelEl.textContent = mainLabel.textContent.trim();
        }
      }
    }
  );
}
